/**
 * platform/client.ts — SENDING A CONSOLE REQUEST, AND THE FOUR ANSWERS IT
 * DISTINGUISHES.
 *
 * ===========================================================================
 * `credentials: 'omit'` IS THE PLACEMENT DECISION, IN ONE WORD
 * ===========================================================================
 * `@cwos/api-client` sets `credentials: 'include'` on every call, because the
 * customer application authenticates with the `cwos_session` cookie and needs
 * the browser to attach it. THIS CONSOLE MUST NOT USE THAT PACKAGE, and the
 * reason is not style: the two authority domains do not merge (two-authority
 * ruling, 2026-09-07), and a fetch that attaches a customer's session cookie to
 * a platform publication request is that merge happening in the network layer.
 *
 * `omit` also protects the OPERATOR. If the platform API is ever reachable on a
 * host where an operator's browser holds a customer session — a shared
 * hostname, a staging box, a proxy — `include` would attach that cookie, and
 * the server's tenant wall (`knowledge/global/routes.ts`) would answer 404 to a
 * request that was otherwise perfectly authorized. Sending no cookie at all
 * means the console's requests are decided by the platform credential and
 * nothing else, which is what the door is for.
 *
 * ===========================================================================
 * FOUR ANSWERS, KEPT APART
 * ===========================================================================
 * Collapsing these is this codebase's most common defect, and a console is
 * where it costs the most: an operator who cannot tell "the door is not
 * configured" from "your credential is wrong" from "that requirement is not
 * there" from "the state does not permit this" will retry the wrong thing.
 *
 *   ABSENT           404. The route does not exist, OR the record does not.
 *                    The server answers 404 for a misconfigured
 *                    SYSTEM_TRIGGER_TOKEN too, ON PURPOSE — a misconfigured
 *                    environment has no publication surface rather than an
 *                    unauthenticated one — so the console must NOT translate
 *                    404 into "not found" and leave it there. It says both
 *                    readings, because it genuinely cannot tell them apart and
 *                    guessing would be worse.
 *   UNAUTHENTICATED  401. The platform credential is missing or wrong.
 *   REFUSED          403/409/422. The database's own wall, carried through
 *                    VERBATIM. Never paraphrased: the message names the thing
 *                    that stopped the caller — "may assist discovery but may
 *                    not become an external requirement", "is already retired;
 *                    a version leaves force once" — and a paraphrase here would
 *                    blur every one of them into "cannot publish".
 *   INVALID          400. The request was malformed; `issues` names the fields.
 *
 * Anything else is `TRANSPORT` and says so. A network failure rendered as
 * "no results" is a lie about the world.
 */
import type { ConsoleRequest } from './requests.js';
import type { PlatformActor } from './credentials.js';

export type ConsoleOutcome<T> =
  | { kind: 'ok'; value: T }
  | {
      /**
       * 404. TWO READINGS AND THE CONSOLE HOLDS BOTH. It is either "no such
       * record" or "this door is not configured in this environment" — the
       * server deliberately answers the same way to each, so that a
       * misconfigured deployment does not advertise a publication surface.
       */
      kind: 'absent';
      message: string;
    }
  | { kind: 'unauthenticated'; message: string }
  | { kind: 'refused'; status: number; message: string }
  | { kind: 'invalid'; message: string; issues: { path: string; message: string }[] }
  | { kind: 'transport'; message: string };

export interface Transport {
  (input: string, init: RequestInit): Promise<Response>;
}

export interface ClientConfig {
  /** ABSOLUTE. The console is never same-origin with the customer app. */
  readonly baseUrl: string;
  readonly transport?: Transport;
}

const OPERATOR_HEADER = 'x-platform-operator';
const TOKEN_HEADER = 'x-system-trigger-token';

/**
 * The headers one console request carries. Exported so the unit suite can
 * assert what is on the wire without a network, and so the ONE place a
 * credential becomes a header is readable in isolation.
 *
 * The token goes in `x-system-trigger-token` and NOT in `Authorization:
 * Bearer`. Both forms are accepted by the server (`readSystemToken`), and the
 * named header is the honest one: `Authorization: Bearer` is the shape the
 * customer application uses for a USER SESSION, and a console that spelled a
 * shared platform secret the same way would make the two indistinguishable in
 * a proxy log, a HAR file, or a screenshot.
 */
export function requestHeaders(request: ConsoleRequest, actor: PlatformActor): HeadersInit {
  const headers: Record<string, string> = {
    accept: 'application/json',
    [TOKEN_HEADER]: actor.systemToken,
    [OPERATOR_HEADER]: actor.operatorId,
  };
  if (request.body !== undefined) headers['content-type'] = 'application/json';
  return headers;
}

interface ErrorBody {
  message?: unknown;
  issues?: unknown;
}

function messageOf(body: ErrorBody | null, fallback: string): string {
  return typeof body?.message === 'string' && body.message.trim() ? body.message : fallback;
}

function issuesOf(body: ErrorBody | null): { path: string; message: string }[] {
  if (!Array.isArray(body?.issues)) return [];
  return body.issues.flatMap((issue) => {
    if (typeof issue !== 'object' || issue === null) return [];
    const { path, message } = issue as { path?: unknown; message?: unknown };
    return typeof path === 'string' && typeof message === 'string' ? [{ path, message }] : [];
  });
}

export function createConsoleClient(config: ClientConfig) {
  const send = config.transport ?? ((input: string, init: RequestInit) => fetch(input, init));

  return async function call<T>(
    request: ConsoleRequest,
    actor: PlatformActor,
  ): Promise<ConsoleOutcome<T>> {
    let response: Response;
    try {
      response = await send(`${config.baseUrl}${request.path}`, {
        method: request.method,
        headers: requestHeaders(request, actor),
        // THE LINE THIS MODULE EXISTS FOR. See the docblock.
        credentials: 'omit',
        ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
      });
    } catch (err) {
      return {
        kind: 'transport',
        message: `The platform API could not be reached: ${err instanceof Error ? err.message : 'unknown error'}`,
      };
    }

    let body: (ErrorBody & Record<string, unknown>) | null = null;
    try {
      body = (await response.json()) as ErrorBody & Record<string, unknown>;
    } catch {
      // A non-JSON body is not an empty one. Leaving `body` null and falling
      // through to the status-derived message says "the server answered and
      // the console could not read it", which is true.
      body = null;
    }

    if (response.ok) return { kind: 'ok', value: body as T };

    switch (response.status) {
      case 400:
        return {
          kind: 'invalid',
          message: messageOf(body, 'The request was rejected as malformed.'),
          issues: issuesOf(body),
        };
      case 401:
        return {
          kind: 'unauthenticated',
          message: messageOf(body, 'The platform credential was not accepted.'),
        };
      case 404:
        return {
          kind: 'absent',
          message: messageOf(
            body,
            'Not found. Either the record does not exist, or the platform door is not ' +
              'configured in this environment — the server answers the same way to both.',
          ),
        };
      default:
        return {
          kind: 'refused',
          status: response.status,
          message: messageOf(body, `Refused (${response.status}).`),
        };
    }
  };
}

export type ConsoleClient = ReturnType<typeof createConsoleClient>;
